import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { API_URL, getAuthHeader } from '../../utils/config';

export default function GroupInfoPanel({ conversation, currentUser, users, onClose, onLeave, onUpdate }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [groupName, setGroupName] = useState(conversation?.name || '');
  const [groupDesc, setGroupDesc] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [viewingAvatar, setViewingAvatar] = useState(null);
  const avatarRef = useRef(null);

  // Number() both sides — currentUser.id can be a string from localStorage,
  // so strict === silently failed and hid the admin controls.
  const myMembership = members.find(m => Number(m.id) === Number(currentUser?.id));
  const amAdmin = myMembership?.is_admin;

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/chat/conversations/${conversation.id}/members`,
        { headers: getAuthHeader() }
      );
      setMembers(data);
      const conv = await axios.get(`${API_URL}/chat/conversations`, { headers: getAuthHeader() });
      const thisConv = conv.data.find(c => c.id === conversation.id);
      if (thisConv?.description) setGroupDesc(thisConv.description);
    } catch(e) {}
    finally { setLoading(false); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const { data } = await axios.post(
        `${API_URL}/chat/conversations/${conversation.id}/avatar`, fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      onUpdate({ ...conversation, avatar_url: data.avatar_url });
      toast.success('Group photo updated!');
    } catch(e) { toast.error('Failed to update photo'); }
  };

  const saveGroupInfo = async () => {
    try {
      await axios.put(
        `${API_URL}/chat/conversations/${conversation.id}`,
        { name: groupName, description: groupDesc },
        { headers: getAuthHeader() }
      );
      onUpdate({ ...conversation, name: groupName });
      setEditingName(false);
      toast.success('Group updated!');
    } catch(e) { toast.error('Failed'); }
  };

  const makeAdmin = async (userId, isAdmin) => {
    try {
      await axios.put(
        `${API_URL}/chat/conversations/${conversation.id}/members/${userId}/admin`,
        { is_admin: isAdmin }, { headers: getAuthHeader() }
      );
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, is_admin: isAdmin } : m));
      toast.success(isAdmin ? 'Made admin' : 'Removed admin role');
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const removeMember = async (userId) => {
    // Same string/number mismatch: when this was false, leaving a group took
    // the 'remove member' branch — the API call succeeded but onLeave() never
    // fired, so the panel stayed open and the user thought nothing happened.
    const isSelf = Number(userId) === Number(currentUser?.id);
    if (!window.confirm(isSelf ? 'Leave this group?' : 'Remove this member?')) return;
    try {
      await axios.delete(
        `${API_URL}/chat/conversations/${conversation.id}/members/${userId}`,
        { headers: getAuthHeader() }
      );
      if (isSelf) { onLeave(); onClose(); }
      else { setMembers(prev => prev.filter(m => m.id !== userId)); toast.success('Member removed'); }
    } catch(e) { toast.error('Failed'); }
  };

  const addMember = async (userId) => {
    try {
      await axios.post(
        `${API_URL}/chat/conversations/${conversation.id}/members`,
        { user_id: userId }, { headers: getAuthHeader() }
      );
      const newUser = users.find(u => u.id === userId);
      if (newUser) setMembers(prev => [...prev, { ...newUser, is_admin: false }]);
      toast.success('Member added!');
      setShowAddMember(false);
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const notInGroup = users.filter(u =>
    !members.find(m => m.id === u.id) &&
    u.full_name.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex flex-col h-full overflow-hidden">

      {/* Hero */}
      <div className="relative" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
        <div className="px-4 pt-3 pb-10 flex flex-col items-center">
          <div className="w-full flex justify-between items-center mb-3">
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            </button>
            <span className="text-white/80 text-xs font-semibold tracking-wide">Group Info</span>
            <div className="w-8"/>
          </div>

          {/* Group avatar */}
          <div className="relative mb-2">
            <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white/40 shadow-xl border-2 border-white/20">
              {(avatarPreview || conversation?.avatar_url)
                ? <img src={avatarPreview || (conversation.avatar_url?.startsWith('https://') ? conversation.avatar_url : `https://qtalk.qualtechedge.in${conversation.avatar_url}`)} className="w-full h-full object-cover cursor-zoom-in" alt=""
                    onClick={() => { const src = avatarPreview || (conversation.avatar_url?.startsWith('https://') ? conversation.avatar_url : `https://qtalk.qualtechedge.in${conversation.avatar_url}`); if(src) setViewingAvatar(src); }}/>
                : <div className="w-full h-full flex items-center justify-center bg-white/20">
                    <span className="text-3xl font-bold text-white">{conversation?.name?.[0]?.toUpperCase()}</span>
                  </div>}
            </div>
            {amAdmin && (
              <label className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center cursor-pointer shadow-lg border-2 border-teal-100 hover:bg-teal-50 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-teal-600">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/>
                </svg>
                <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange}/>
              </label>
            )}
          </div>

          {/* Group name */}
          {editingName ? (
            <div className="flex items-center gap-2 w-full px-4">
              <input value={groupName} onChange={e => setGroupName(e.target.value)}
                className="flex-1 bg-white/20 text-white placeholder-white/50 rounded-xl px-3 py-1.5 text-sm font-bold focus:outline-none focus:bg-white/30 transition"
                autoFocus onKeyDown={e => { if(e.key==='Enter') saveGroupInfo(); if(e.key==='Escape') setEditingName(false); }}/>
              <button onClick={saveGroupInfo} className="w-7 h-7 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <h3 className="font-bold text-white text-base">{groupName}</h3>
              {amAdmin && (
                <button onClick={() => setEditingName(true)} className="text-white/60 hover:text-white transition">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              )}
            </div>
          )}
          <p className="text-white/60 text-xs mt-0.5">Group · {members.length} members</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-5 bg-white dark:bg-gray-900 rounded-t-3xl"/>
      </div>

      {/* Description */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        {amAdmin ? (
          <textarea value={groupDesc} onChange={e => setGroupDesc(e.target.value)}
            onBlur={saveGroupInfo}
            placeholder="Add group description..."
            rows={2} className="w-full text-sm text-gray-600 dark:text-gray-300 bg-transparent placeholder-gray-400 focus:outline-none resize-none"/>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            {groupDesc || 'No description'}
          </p>
        )}
      </div>

      {/* Members list */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-2.5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{members.length} Members</p>
          {amAdmin && (
            <button onClick={() => setShowAddMember(p => !p)}
              className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 font-semibold transition">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Member
            </button>
          )}
        </div>

        {/* Add member search */}
        {showAddMember && (
          <div className="mx-3 mb-2 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-gray-400"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Search people to add..." value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)} autoFocus
                className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none"/>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {notInGroup.length === 0
                ? <p className="text-center text-xs text-gray-400 py-4">No users to add</p>
                : notInGroup.map(u => (
                  <div key={u.id} onClick={() => addMember(u.id)}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                      {u.avatar_url
                        ? <img src={u.avatar_url?.startsWith('https://') ? u.avatar_url : `https://qtalk.qualtechedge.in${u.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                        : u.full_name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{u.full_name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.designation || u.email}</p>
                    </div>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-teal-500 flex-shrink-0"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 rounded-full border-2 border-teal-200 border-t-teal-500 animate-spin"/></div>
        ) : (
          members.map(m => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
                {m.avatar_url
                  ? <img src={m.avatar_url?.startsWith('https://') ? m.avatar_url : `https://qtalk.qualtechedge.in${m.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                  : m.full_name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {m.full_name} {m.id === currentUser?.id ? '(You)' : ''}
                  </p>
                  {m.is_admin && (
                    <span className="text-xs px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-md font-medium flex-shrink-0">Admin</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 truncate">{m.designation || m.department || 'Member'}</p>
              </div>

              {/* Member actions */}
              {amAdmin && m.id !== currentUser?.id && (
                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition">
                  <button onClick={() => makeAdmin(m.id, !m.is_admin)}
                    className={`text-xs px-2 py-1 rounded-lg font-medium transition ${m.is_admin ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-red-50 hover:text-red-500' : 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 hover:bg-teal-100'}`}
                    title={m.is_admin ? 'Remove admin' : 'Make admin'}>
                    {m.is_admin ? 'Demote' : 'Admin'}
                  </button>
                  <button onClick={() => removeMember(m.id)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                    title="Remove from group">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                      <line x1="18" y1="18" x2="22" y2="22"/><line x1="22" y1="18" x2="18" y2="22"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
        <button onClick={() => removeMember(currentUser?.id)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Leave Group
        </button>
      </div>

      {/* Avatar lightbox */}
      {viewingAvatar && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" onClick={() => setViewingAvatar(null)}>
          <img src={viewingAvatar} alt="Group avatar" className="max-w-sm max-h-96 rounded-2xl shadow-2xl object-contain"/>
          <button onClick={() => setViewingAvatar(null)} className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition">✕</button>
        </div>
      )}
    </div>
  );
}
